<template>
    <DCard
        class="fulfillment-requests"
        padding
        footer-padding
    >
        <ArchiveModeSwitch
            class="mb-3"
            :model-value="archiveMode"
            :show-type-select="false"
            :reference-id="filter.archiveContract || ''"
            :reference-id-error="errors.archiveContract"
            :read-failed="archiveReadFailed"
            @update:modelValue="onArchiveToggle"
            @update:referenceId="(value) => { filter.archiveContract = value || null }"
            @apply="applyArchiveFilters"
        />
        <DRow
            class="mb-3"
            align-vertical="end"
        >
            <DCol
                sm="8"
            >
                <div
                    v-if="!archiveMode"
                    class="filter-buttons"
                >
                    <template
                        v-for="group in moduleGroups"
                        :key="group.key"
                    >
                        <!-- The tooltip sits on a wrapper because a disabled button dispatches no
                             mouse events, and v-c-tooltip renders an empty bubble for empty
                             content, so it is only applied to the groups that need it. -->
                        <span
                            v-if="group.modules.length === 0"
                            v-c-tooltip="__('tools.fulfillment.group_modules_inactive')"
                        >
                            <DButton
                                :id="group.key"
                                :label="__('tools.fulfillment.' + group.key)"
                                mini
                                disabled
                            />
                        </span>
                        <DButton
                            v-else
                            :id="group.key"
                            :label="__('tools.fulfillment.' + group.key)"
                            mini
                            @click="filterByModules(group.modules)"
                        />
                    </template>
                    <DButton
                        id="all"
                        :label="__('tools.fulfillment.all')"
                        mini
                        @click="getAll()"
                    />
                </div>
                <DAlert
                    v-if="showNewJobHint && !archiveMode"
                    color="success"
                    class="d-flex align-items-center new-job-hint"
                    @click="refreshPage"
                >
                    <DIcon
                        name="alert"
                        :size="24"
                    />
                    <div class="pr-2">
                        {{ __('tools.fulfillment.new_job_hint') }}
                    </div>
                </DAlert>
            </DCol>
            <DCol
                sm="4"
                align-vertical="end"
            >
                <DButton
                    id="table-configuration"
                    icon="cog-outline"
                    :label="__('messages.list_configuration')"
                    class="float-end"
                    @click="showListViewModal = true"
                />
            </DCol>
        </DRow>
        <!--
            One table for both modes: archive mode drops the column filters, the sorter and the
            row actions, but every cell renders through the same templates. The sorter is off
            rather than inert, since the archive can only order `updated_at desc, id desc`.

            The `v-if="!archiveMode"` on each `-filter` slot below and the `key` go together.
            DTable captures the slot set once in setup and teleports each filter into
            `tr:nth-child(2) th:nth-child(N)`, a row `column-filter=false` does not render. So the
            slots must be gone in archive mode (else they teleport into null and blow up on
            unmount) and the table must remount on the way back (else the empty capture stands and
            CSmartTable's own filter inputs replace ours).

            `archiveItems` follows the server `archive` prop rather than the toggle, so the list
            is empty until Apply.
        -->
        <DTable
            :key="archiveMode ? 'archive' : tableKey"
            class="fulfillment-table-scroller"
            :column-filter="!archiveMode"
            :items="archiveMode ? archiveItems : requestsData"
            :fields="archiveMode ? archiveFields : getFields"
            :sorter="archiveMode ? false : isSortable"
            :sorter-value="archiveMode ? undefined : sorterValue"
            :no-items-label="archiveMode ? archiveNoItemsLabel : undefined"
            @update:sorter-value="updateRequests"
        >
            <template #action="{item}">
                <td>
                    {{ item.action.name }}
                </td>
            </template>
            <template #module="{item}">
                <td>
                    {{ item.filter }}
                </td>
            </template>
            <template #created_at="{item}">
                <td>
                    {{ setDateFormat(item.created_at, 'YYYY-MM-DD hh:mm:ss A') }}
                </td>
            </template>
            <template #updated_at="{item}">
                <td>
                    {{ setDateFormat(item.updated_at, 'YYYY-MM-DD hh:mm:ss A') }}
                </td>
            </template>
            <template #customer="{item}">
                <td v-if="userPermissions.includes('customer.view')">
                    <Link :href="customerProfileUrl(item.job_reference.customer.id)">
                        <span v-if="item.job_reference.customer.company">{{ item.job_reference.customer.company }}</span>
                        <br v-if="item.job_reference.customer.company">
                        <span v-if="item.job_reference.customer.firstname">{{
                            item.job_reference.customer.firstname
                        }}</span>
                        <span v-if="item.job_reference.customer.lastname">{{
                            item.job_reference.customer.lastname
                        }}</span>
                        <span>({{ item.job_reference.customer.id }})</span>
                    </Link>
                </td>
                <td v-else>
                    <span v-if="item.job_reference.customer.company">{{ item.job_reference.customer.company }}</span>
                    <br v-if="item.job_reference.customer.company">
                    <span v-if="item.job_reference.customer.firstname">{{
                        item.job_reference.customer.firstname
                    }}</span>
                    <span v-if="item.job_reference.customer.lastname">{{
                        item.job_reference.customer.lastname
                    }}</span>
                    <span>({{ item.job_reference.customer.id }})</span>
                </td>
            </template>
            <template #contract="{item}">
                <td v-if="userPermissions.includes('contract.view')">
                    <Link :href="customerContractsUrl(item.job_reference)">
                        <template v-if="item.job_reference.domain_name">
                            {{ item.job_reference.domain_name }}
                            <br>
                            ({{ item.job_reference.id }})
                        </template>
                        <template v-else>
                            {{ item.product.name }} ({{ item.job_reference.id }})
                        </template>
                    </Link>
                </td>
                <td v-else>
                    <template v-if="item.job_reference.domain_name">
                        {{ item.job_reference.domain_name }}
                        <br>
                        ({{ item.job_reference.id }})
                    </template>
                    <template v-else>
                        {{ item.product.name }} ({{ item.job_reference.id }})
                    </template>
                </td>
            </template>
            <template #last_error="{item}">
                <td class="text-break last-error">
                    <div
                        v-for="error in nl2array(item.last_error)"
                        :key="error"
                    >
                        {{ error }}
                    </div>
                </td>
            </template>
            <template #status="{item}">
                <td>{{ __('tools.fulfillment.status_' + item.status) }}</td>
            </template>
            <template #available_at="{item}">
                <td>
                    <span
                        v-if="item.available_at.length > 0"
                        v-c-tooltip="{
                            content: item.available_at_date
                        }"
                    >
                        {{ item.available_at }}
                    </span>
                </td>
            </template>
            <template
                v-if="!archiveMode"
                #status-filter
            >
                <DMultiSelect
                    id="status-dropdown"
                    :options="statusOptions"
                    selection-type="text"
                    :selected="filter.status"
                    @update="(values) => onFilterUpdate('status', values)"
                />
            </template>
            <template
                v-if="!archiveMode"
                #module-filter
            >
                <DMultiSelect
                    id="module-dropdown"
                    :options="moduleOptions"
                    selection-type="text"
                    :selected="filter.module"
                    @update="(values) => onFilterUpdate('module', values)"
                />
            </template>
            <template
                v-if="!archiveMode"
                #action-filter
            >
                <DMultiSelect
                    id="action-dropdown"
                    :options="actionOptions"
                    selection-type="text"
                    :selected="filter.action"
                    @update="(values) => onFilterUpdate('action', values)"
                />
            </template>
            <template
                v-if="!archiveMode"
                #created_at-filter
            >
                <DDateFormatInput
                    id="date-from"
                    format="YYYY-MM-DD"
                    :model-value="selectedCreatedAt"
                    type="date"
                    @update:modelValue="(value) => { onCreatedAtFilterChange(value)}"
                />
            </template>
            <template
                v-if="!archiveMode"
                #updated_at-filter
            >
                <DDateFormatInput
                    id="date-from"
                    format="YYYY-MM-DD"
                    :model-value="selectedUpdatedAt"
                    type="date"
                    @update:modelValue="(value) => { onUpdatedAtFilterChange(value)}"
                />
            </template>
            <template
                v-if="!archiveMode"
                #customer-filter
            >
                <input
                    id="customer"
                    class="form-control"
                    type="number"
                    :value="filter.customer"
                    pattern="^\d*$"
                    min="1"
                    @change="(event) => {onFilterUpdate('customer', event.target.value)}"
                >
            </template>
            <template
                v-if="!archiveMode"
                #contract-filter
            >
                <input
                    id="contract"
                    class="form-control"
                    type="number"
                    :value="filter.contract"
                    pattern="^\d*$"
                    min="1"
                    @change="(event) => {onFilterUpdate('contract', event.target.value)}"
                >
            </template>
            <template #actions="{item}">
                <td style="display: flex; grid-gap: 5px;">
                    <DButton
                        icon="eye"
                        @click="onRowClicked(item)"
                    />
                    <!-- No live row left to retry, pause, mark as done or withdraw. -->
                    <DDropdown
                        v-if="!archiveMode && item.status !== 'completed' && item.has_actions"
                        :id="`actions_dropdown_${item.reference_id}`"
                        :class="'more-actions-button'"
                        :caret="false"
                        icon="dots-horizontal"
                    >
                        <DDropdownItem
                            v-if="item.status === 'failed'"
                            :id="`action_retry_${item.reference_id}`"
                            @click="handleRetry(item, retryRoute)"
                        >
                            {{ __('tools.fulfillment.retry') }}
                        </DDropdownItem>
                        <DDropdownItem
                            v-if="item.status === 'failed' && item.can_ignore_duplicate_domain_checks_on_retry"
                            :id="`action_retry_without_duplicate_domain_checks_${item.reference_id}`"
                            @click="handleRetryWithoutDuplicateDomainCheck(item)"
                        >
                            {{ __('tools.fulfillment.retry_without_duplicate_domain_check') }}
                        </DDropdownItem>
                        <DDropdownItem
                            v-if="item.status === 'retry'"
                            :id="`action_retry_now_${item.reference_id}`"
                            @click="handleRetry(item, retryNowRoute)"
                        >
                            {{ __('tools.fulfillment.retry_now') }}
                        </DDropdownItem>
                        <DDropdownItem
                            v-if="item.status !== 'completed' && item.status !== 'paused' && item.status !== 'failed' && item.status !== 'cancelled'"
                            :id="`action_pause_${item.reference_id}`"
                            @click="pause(item.id)"
                        >
                            {{ __('tools.fulfillment.pause') }}
                        </DDropdownItem>
                        <DDropdownItem
                            v-if="item.status === 'paused'"
                            :id="`action_resume_${item.reference_id}`"
                            @click="resume(item.id)"
                        >
                            {{ __('tools.fulfillment.resume') }}
                        </DDropdownItem>
                        <DDropdownItem
                            v-if="item.status !== 'cancelled'"
                            :id="`action_mark_as_done_${item.reference_id}`"
                            @click="openMarkAsDoneModal(item)"
                        >
                            {{ __('tools.fulfillment.mark_as_done') }}
                        </DDropdownItem>
                        <DDropdownItem
                            v-if="(item.job_reference.status === 'wait_for_fulfillment' || item.job_reference.status === 'wait_for_input') && item.status !== 'cancelled'"
                            :id="`action_withdraw_${item.reference_id}`"
                            @click="withdrawContract(item)"
                        >
                            {{ __('tools.fulfillment.withdraw_contract') }}
                        </DDropdownItem>
                        <DDropdownItem
                            v-if="item.status === 'failed' || item.status === 'retry'"
                            :id="`action_cancel_${item.reference_id}`"
                            @click="cancel(item.id)"
                        >
                            {{ __('tools.fulfillment.cancel') }}
                        </DDropdownItem>
                    </DDropdown>
                </td>
            </template>
        </DTable>

        <!--
            No reset in archive mode: it clears the live filter set, none of which is showing --
            the column filters are gone and the lookup is the switch's own field with its own
            Apply. The v-if sits on the slot, not the button, so DCard drops the footer
            (`hasFooterSlot`) rather than leaving an empty padded band.
        -->
        <template
            v-if="!archiveMode"
            #footer
        >
            <DButton
                id="filter-reset"
                icon="filter-off"
                :label="__('messages.actions.reset_filter')"
                @click="resetFilters"
            />
        </template>

        <DPagination
            v-if="paginationVisible"
            class="pb-2"
            :active-page="requests.current_page"
            :pages="requests.last_page"
            align="center"
            @update:activePage="changePage"
        />
        <MarkRequestDoneModal
            v-if="showMarkAsDoneModal"
            :request="currentRequest"
            :show-modal="showMarkAsDoneModal"
            :external-id-mapping="externalIdMapping"
            :filter="filter"
            @close-modal="closeMarkAsDoneModal"
            @confirm-modal="markRequestAsDone"
        />
        <FulfillmentRequestDetailsModal
            v-if="shouldRenderModal"
            :show-modal="showModal"
            :request="requestForDetails"
            :archived="archive"
            @close-modal="closeModal"
        />
        <WithdrawContractModal
            :show-modal="showWithdrawConfirmationModal"
            @confirm-modal="withdrawContractConfirm"
            @close-modal="showWithdrawConfirmationModal = false"
        />
        <DModal
            v-if="showRetryModal"
            v-model:show="showRetryModal"
            :title="__('tools.fulfillment.retry_modal_title')"
            :message="__('tools.fulfillment.retry_modal_message')"
            is-confirm
            @confirm="retry"
            @close="handleRetryModalClose"
        >
            <DCard v-if="selectedRequest.action.name === 'create'">
                <DCardBody>
                    <DFormRow
                        :label="''"
                        :hint="__('tools.fulfillment.clear_modal_message_info')"
                        is-vertical
                    >
                        <DCol>
                            <DFormCheck
                                id="with_restart"
                                v-model="withRestart"
                                :label="__('tools.fulfillment.clear_modal_message')"
                                inline
                            />
                        </DCol>
                    </DFormRow>
                </DCardBody>
            </DCard>
        </DModal>
        <DModal
            is-confirm
            is-error
            :title="__('tools.fulfillment.confirm_retry_without_duplicate_domain_check.title')"
            :show="showConfirmIgnoreDuplicateDomainCheckModal"
            @close="handleRetryWithoutDuplicateDomainCheckClose"
            @confirm="retry(true)"
        >
            <DCard>
                <DCardBody>
                    {{ __('tools.fulfillment.confirm_retry_without_duplicate_domain_check.text') }}
                </DCardBody>
            </DCard>
        </DModal>
        <ListViewModal
            v-if="showListViewModal"
            :show-modal="showListViewModal"
            :user-list-view="userListView"
            @close-modal="showListViewModal = false"
        />
    </DCard>
</template>

<script>
import ArchiveModeSwitch from '~@/Components/Common/Archive/ArchiveModeSwitch.vue'
import FulfillmentRequestDetailsModal from '~@/Components/Fulfillment/RequestDetailsModal'
import WithdrawContractModal from '~@/Components/Fulfillment/WithdrawContractModal'
import MarkRequestDoneModal from '~@/Components/Fulfillment/MarkRequestDoneModal'
import nl2array from '~@/Mixins/nl2array'
import ListViewModal from '~@/Components/ListView/ListViewModal'

export default {
    name: 'FulfillmentIndex',
    components: {
        ArchiveModeSwitch,
        ListViewModal,
        MarkRequestDoneModal,
        WithdrawContractModal,
        FulfillmentRequestDetailsModal
    },
    mixins: [nl2array],
    props: {
        requests: {
            type: Object,
            default: () => {
                return {}
            }
        },
        modules: {
            type: Array,
            default: () => {
                return []
            }
        },
        moduleGroups: {
            type: Array,
            default: () => {
                return []
            }
        },
        status: {
            type: Array,
            default: () => {
                return []
            }
        },
        actions: {
            type: Array,
            default: () => {
                return []
            }
        },
        errors: {
            type: Object,
            default () {
                return {}
            }
        },
        externalIdMapping: {
            type: Object,
            default () {
                return {}
            }
        },
        userListView: {
            type: Array,
            default: () => {
                return []
            }
        },
        filters: {
            type: Object,
            default () {
                return {}
            }
        },
        archive: {
            type: Boolean,
            default: false
        },
        archiveContract: {
            type: [Number, String],
            default: null
        },
        archiveReadFailed: {
            type: Boolean,
            default: false
        }
    },
    data () {
        const params = new URLSearchParams(window.location.search)
        const action = this.getParamArray('action')
        const module = this.getParamArray('module')
        const status = this.getParamArray('status')
        const createdAtFrom = params.has('createdAtFrom') ? params.get('createdAtFrom') : null
        const createdAtTo = params.has('createdAtTo') ? params.get('createdAtTo') : null
        const updatedAtFrom = params.has('updatedAtFrom') ? params.get('updatedAtFrom') : null
        const updatedAtTo = params.has('updatedAtTo') ? params.get('updatedAtTo') : null
        const customer = params.has('customer') ? params.get('customer') : null
        const contract = params.has('contract') ? params.get('contract') : null
        return {
            filter: {
                action,
                module,
                status,
                createdAtFrom,
                createdAtTo,
                updatedAtFrom,
                updatedAtTo,
                customer,
                contract,
                order_by: this.filters.order_by,
                order_by_asc: this.filters.order_by_asc,
                // Every visit goes through `_query: this.filter`, so the mode lives here or paging
                // drops back to live data. Ziggy encodes booleans as 1/0 and skips nulls, so
                // `null` leaves live URLs untouched.
                archive: this.archive ? true : null,
                // Its own key, not the `contract` filter above: sharing one left each mode
                // filtering the other's list. Rides every visit like `archive`, and never saved
                // -- FulfillmentOverviewFilter emits only the live keys.
                archiveContract: this.archiveContract || null,
                reset: false
            },
            defaultStatuses: [
                'failed',
                'queued',
                'retry',
                'paused'
            ],
            showModal: false,
            showMarkAsDoneModal: false,
            showConfirmIgnoreDuplicateDomainCheckModal: false,
            currentRequest: {},
            requestForDetails: {},
            showWithdrawConfirmationModal: false,
            selectedRequest: null,
            errorsData: this.errors,
            showRetryModal: false,
            withRestart: false,
            retryRoute: 'tools.fulfillment.retry',
            retryNowRoute: 'tools.fulfillment.retryNow',
            selectedRetryRoute: null,
            showNewJobHint: false,
            // Local, not the `archive` prop -- see ArchiveModeSwitch. A rejected lookup also
            // counts as on: validation redirects to a URL with no `archive`, so the switch would
            // otherwise snap back to live and take the error message off screen with it.
            archiveMode: this.archive || Boolean(this.errors.archiveContract),
            requestsData: [],
            tableKey: 0,
            showListViewModal: false
        }
    },
    computed: {
        moduleOptions () {
            const module = this.getParamArray('module')

            return this.modules.map((value) => {
                return {
                    value,
                    text: value,
                    selected: module.includes(value)
                }
            })
        },
        statusOptions () {
            let status = this.getParamArray('status')

            if (status.length === 0) {
                status = this.defaultStatuses
            }

            return this.status.map((value) => {
                return {
                    value,
                    text: this.__('tools.fulfillment.status_' + value),
                    selected: status.includes(value)
                }
            })
        },
        actionOptions () {
            const action = this.getParamArray('action')

            return this.actions.map((value) => {
                return {
                    value,
                    text: value,
                    selected: action.includes(value)
                }
            })
        },
        selectedCreatedAt () {
            return this.filter.createdAtFrom?.split(' ')[0]
        },
        selectedUpdatedAt () {
            return this.filter.updatedAtFrom?.split(' ')[0]
        },
        shouldRenderModal () {
            return this.showModal && Object.keys(this.requestForDetails).length > 0
        },
        userPermissions () {
            return this.$page.props.auth.permissions
        },
        isSortable () {
            if (!this.sorterValue.column) return false

            return { external: true }
        },
        sorterValue () {
            if (!this.filter.order_by) return { column: 'updated_at', asc: false }

            return {
                column: this.filter.order_by,
                asc: !!this.filter.order_by_asc
            }
        },
        // Same gate as the rows: until an archive visit happens the props still hold the live
        // result set, so a live page count would sit under an empty archive table.
        paginationVisible () {
            if (this.archiveMode && !this.archive) {
                return false
            }

            return this.requests.last_page > 1
        },
        // Empty until the server has actually rendered archive mode -- see the table comment.
        archiveItems () {
            return this.archive ? this.requests.data : []
        },
        archiveNoItemsLabel () {
            return this.archive && this.filter.archiveContract
                ? this.__('messages.archive.no_records')
                : this.__('messages.archive.contract_hint')
        },
        // The user's configured columns with the per-column sorter and filter flags cleared.
        archiveFields () {
            return this.getFields.map((field) => ({ ...field, sorter: false, filter: false }))
        },
        getFields () {
            const availableList = []
            const removeFromFilter = ['last_error', 'available_at', 'actions']
            this.userListView.forEach((item) => {
                if (item.show_column === true) {
                    availableList.push({
                        key: item.column,
                        label: item.column !== 'actions' && item.column !== 'icon' ? this.__('settings.list_view.' + item.list + '.' + item.column) : '',
                        sorter: item.sort_column_enabled,
                        _style: item.width !== null ? { minWidth: item.width + 'px', maxWidth: item.width + 'px' } : {},
                        filter: !removeFromFilter.includes(item.column)
                    })
                }
            })

            return availableList
        }
    },
    watch: {
        '$page.props.requests.data': {
            handler () {
                this.requestsData = this.requests.data
            }
        },
    },
    created () {
        window.laravelEcho.channel('onecrm.contracts.fulfillment').listen('.fulfillment.job_updated', socketEvent => {
            this.handleRowUpdate(socketEvent)
        })

        const firstTimeOnPage = !new URLSearchParams(window.location.search).has('reset')

        if (firstTimeOnPage) {
            this.setStatues()
        }

        this.requestsData = this.requests.data
    },
    methods: {
        onFilterUpdate (type, value) {
            this.filter[type] = value || null
            this.$inertia.get(this.$route('tools.fulfillment.index', { _query: this.filter }))
        },
        // On reads nothing yet -- the switch shows the hint until the contract id is set.
        // Off goes straight back to live.
        onArchiveToggle (value) {
            this.archiveMode = value

            if (!value) {
                this.filter.archive = null
                this.filter.archiveContract = null
                this.$inertia.get(this.$route('tools.fulfillment.index', { _query: this.filter }))

                return
            }

            this.filter.archive = true
        },
        applyArchiveFilters () {
            this.filter.archive = true
            // Page 1: the previous page number belongs to a different result set.
            delete this.filter.page
            this.$inertia.get(this.$route('tools.fulfillment.index', { _query: this.filter }))
        },
        getParamArray (paramName) {
            const params = new URLSearchParams(window.location.search)
            const paramArray = []
            const expression = new RegExp('^' + paramName + '[\\[]\\d*[\\]]$')
            for (const param of params.entries()) {
                if (param[0].match(expression)) {
                    paramArray.push(param[1])
                }
            }
            return paramArray
        },
        onUpdatedAtFilterChange (value) {
            if (value) {
                this.filter.updatedAtFrom = value + ' 00:00:00'
                this.filter.updatedAtTo = value + ' 23:59:59'
            } else {
                this.filter.updatedAtFrom = this.filter.updatedAtTo = null
            }
            this.$inertia.get(this.$route('tools.fulfillment.index', { _query: this.filter }))
        },
        onCreatedAtFilterChange (value) {
            if (value) {
                this.filter.createdAtFrom = value + ' 00:00:00'
                this.filter.createdAtTo = value + ' 23:59:59'
            } else {
                this.filter.createdAtFrom = this.filter.createdAtTo = null
            }
            this.$inertia.get(this.$route('tools.fulfillment.index', { _query: this.filter }))
        },
        onRowClicked (item) {
            this.requestForDetails = item
            this.showModal = true
        },
        closeModal () {
            this.requestForDetails = null
            this.showModal = false
        },
        handleRetry (request, route) {
            this.selectedRequest = request
            this.showRetryModal = true
            this.selectedRetryRoute = route
        },
        handleRetryModalClose () {
            this.selectedRequest = null
            this.showRetryModal = false
            this.selectedRetryRoute = null
            this.withRestart = false
        },
        handleRetryWithoutDuplicateDomainCheck (request) {
            this.selectedRequest = request
            this.showConfirmIgnoreDuplicateDomainCheckModal = true
            this.selectedRetryRoute = this.retryRoute
        },
        handleRetryWithoutDuplicateDomainCheckClose () {
            this.selectedRequest = null
            this.showConfirmIgnoreDuplicateDomainCheckModal = false
            this.selectedRetryRoute = null
            this.withRestart = false
        },
        retry (ignoreDuplicateDomainCheck) {
            this.$inertia.post(this.$route(this.selectedRetryRoute, { _query: this.filter, jobRequest: this.selectedRequest.id, withRestart: this.withRestart, ignore_duplicate_domain_check: ignoreDuplicateDomainCheck }))
        },
        pause (id) {
            this.$inertia.post(this.$route('tools.fulfillment.pause', { _query: this.filter, jobRequest: id }))
        },
        resume (id) {
            this.$inertia.post(this.$route('tools.fulfillment.resume', { _query: this.filter, jobRequest: id }))
        },
        cancel (id) {
            this.$inertia.post(this.$route('tools.fulfillment.cancel', { _query: this.filter, jobRequest: id }))
        },
        customerProfileUrl (customerId) {
            return this.$route('customers.customers.show', {
                customer: customerId
            })
        },
        customerContractsUrl (contract) {
            return this.$route('customers.customers.contracts.index', {
                customer: contract.customer.id,
                contract: contract.id
            })
        },
        changePage (page) {
            const queryParameters = { _query: this.filter }

            if (page !== 1) {
                queryParameters._query.page = page
            }

            this.$inertia.get(this.$route('tools.fulfillment.index', queryParameters))
        },
        closeMarkAsDoneModal () {
            this.currentRequest = null
            this.showMarkAsDoneModal = false
            this.errorsData = {}
        },
        openMarkAsDoneModal (fulfillmentRequest) {
            this.currentRequest = fulfillmentRequest
            if (fulfillmentRequest.action.name === 'create') {
                this.showMarkAsDoneModal = true
            } else {
                this.$inertia.post(this.$route('tools.fulfillment.markAsDone', {
                    jobRequest: fulfillmentRequest.id,
                    _query: this.filter
                }),
                {
                    action: fulfillmentRequest.action.name
                })
            }
        },
        markRequestAsDone (data) {
            this.currentRequest = null
            this.showMarkAsDoneModal = false
        },
        withdrawContract (request) {
            this.selectedRequest = request
            this.showWithdrawConfirmationModal = true
        },
        withdrawContractConfirm (skipEmail) {
            this.$inertia.post(this.$route('tools.fulfillment.withdraw-contract', {
                jobRequest: this.selectedRequest.id
            }),
            {
                skip_email: skipEmail
            },
            {
                onSuccess: () => {
                    this.showWithdrawConfirmationModal = false
                }
            })
        },
        resetFilters () {
            this.filter.action = null
            this.filter.module = null
            this.filter.status = null
            this.filter.createdAtFrom = null
            this.filter.createdAtTo = null
            this.filter.updatedAtFrom = null
            this.filter.updatedAtTo = null
            this.filter.customer = null
            this.filter.contract = null
            this.filter.reset = true
            this.filter.order_by = null
            this.filter.order_by_asc = 0

            this.$inertia.get(this.$route('tools.fulfillment.index', { _query: this.filter }))
        },
        updateRequests (event) {
            this.filter.order_by = event.column
            this.filter.order_by_asc = event.asc ? 1 : 0

            this.changePage(this.requests.current_page)
        },
        setStatues () {
            this.filter.status = this.defaultStatuses
        },
        filterByModules (groupModules) {
            this.filter.module = [...groupModules]
            this.setStatues()
            this.$inertia.get(this.$route('tools.fulfillment.index', { _query: this.filter }))
        },
        getAll () {
            this.filter.module = null
            this.filter.status = null
            this.$inertia.get(this.$route('tools.fulfillment.index', { _query: this.filter }))
        },
        refreshPage () {
            this.$inertia.get(this.$route('tools.fulfillment.index', { _query: this.filter }))
        },
        handleRowUpdate (socketEvent) {
            // if QueueJobRequest came from different tenant do nothing
            if (socketEvent.tenant !== this.$page.props.tenant) {
                return
            }

            // Archive mode lists a snapshot: a live broadcast has the wrong row shape, and the
            // hint would invite a refresh that changes nothing.
            if (this.archiveMode) {
                return
            }

            const jobRequest = socketEvent.queueJobRequest
            const existingRequestId = this.requestsData.findIndex(existingRequest => existingRequest.id === jobRequest.id)

            // if ID is less than 0, we then now it's a new job and we can just show a hint about it and return
            if (existingRequestId < 0) {
                this.showNewJobHint = true

                return
            }

            // remove highlight-row class from selected row after 3000ms
            setTimeout(() => {
                jobRequest._classes = ''
                this.requestsData.splice(existingRequestId, 1, jobRequest)
                this.tableKey++
            }, 3000)

            jobRequest._classes = 'highlight-row'
            this.requestsData.splice(existingRequestId, 1, jobRequest)
        }
    }
}
</script>

<style lang="scss" scoped>
.filter-buttons {
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
}
.new-job-hint {
    margin-top: 10px;
    cursor: pointer;
}
.more-actions-button {
    background: var(--colorSecondary);
    color: var(--colorSecondaryText);
    border-radius: 5px;
}
.more-actions-button:hover {
    background: var(--colorSecondaryHover);
}
.more-actions-button:active {
    background: var(--colorSecondaryActive);
}
.fulfillment-requests {
    .c-multi-select-options {
        width: auto !important;
    }

    // `text-break` lets an error wrap between any two letters, so without a floor this is the
    // column the table squeezes first.
    .last-error {
        min-width: 150px;
    }

    // The table must not push past the card. CoreUI's `responsive` prop can't do this: app.scss
    // forces `.table-responsive { overflow: visible !important }` app-wide. Same approach as
    // `.jobs-table-scroller` on the Queues/Jobs page. `:deep` because DTable has several roots,
    // so the class lands on an element without this component's scope attribute.
    :deep(.fulfillment-table-scroller) {
        overflow-x: auto;

        // Room for the header filter dropdowns to open into: the scroller becomes their
        // clipping boundary, so a list filtered down to one or two rows would cut them off.
        min-height: var(--multi-select-scroller-min-height);
    }
}
</style>
